import React, { useState, useEffect } from 'react';
import { GameHeader } from './components/GameHeader';
import { Theater } from './components/Theater';
import { PopulationNetwork } from './components/PopulationNetwork';
import { NetworkPanel } from './components/NetworkPanel';
import { EventLog } from './components/EventLog';
import { DebriefModal } from './components/DebriefModal';
import { TutorialOverlay } from './components/TutorialOverlay';
import { FeedbackOverlay } from './components/FeedbackOverlay';
import { ActionBlockedModal } from './components/ActionBlockedModal';
import { MissionScoreboard } from './components/MissionScoreboard';
import { ActionPanel } from './components/ActionPanel';
import { TurnTransitionOverlay } from './components/TurnTransitionOverlay';
import { WelcomeModal } from './components/WelcomeModal';
import { HelpModal } from './components/HelpModal';
import { AdjudicationPanel } from './components/AdjudicationPanel';
import { AdjudicationResults } from './components/AdjudicationResults';
import { TeamLobby } from './components/TeamLobby';
import { TeamDashboard } from './components/TeamDashboard';
import { ScoreBoard } from './components/ScoreBoard';
import { MultiplayerChat } from './components/MultiplayerChat';
import { GameManagerDashboard } from './components/GameManagerDashboard';
import { PasswordGate } from './components/PasswordGate';
import { useGameStore } from './store/gameStore';
import { useAdjudicationStore } from './store/adjudicationStore';
import { SCENARIOS } from './data/sectors';
import { Play, Terminal, HelpCircle, ShieldCheck, Target, Activity, AlertTriangle, ChevronRight, Lock, Network } from 'lucide-react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

function App() {
  // Password Protection State
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('nd_authenticated') === 'true';
  });
  const gameStatus = useGameStore(state => state.gameStatus);
  const startGame = useGameStore(state => state.startGame);
  const tickTimer = useGameStore(state => state.tickTimer);
  const endTurn = useGameStore(state => state.endTurn);
  const performAction = useGameStore(state => state.performAction);
  const selectedSectorId = useGameStore(state => state.selectedSectorId);
  const isLocked = useGameStore(state => state.isLocked);
  
  const adjudicationPhase = useAdjudicationStore(state => state.phase);
  
  const [selectedScenario, setSelectedScenario] = useState(SCENARIOS[0].id);
  const [showHelp, setShowHelp] = useState(false);
  
  // Network View State
  const [viewMode, setViewMode] = useState<'map' | 'network'>('map');
  const [selectedNode, setSelectedNode] = useState<{ id: string, type: 'population' | 'community' } | null>(null);

  // Routing Effect - Force Theater view on /theater route
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === '/theater' && gameStatus !== 'playing') {
      console.log("Route is /theater, forcing game start...");
      startGame(SCENARIOS[0].id, false);
    }
  }, [location, gameStatus, startGame]);

  // Timer Effect
  useEffect(() => {
    const interval = setInterval(() => {
      tickTimer();
    }, 1000);
    return () => clearInterval(interval);
  }, [tickTimer]);

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (gameStatus !== 'playing') return;

      if (e.code === 'Space') {
        e.preventDefault();
        endTurn();
      }
      
      if (selectedSectorId) {
        if (e.key === '1') performAction('Listen');
        if (e.key === '2') performAction('Broadcast');
        if (e.key === '3') performAction('Jam');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameStatus, selectedSectorId, endTurn, performAction]);

  // Show password gate if not authenticated
  if (!isAuthenticated) {
    return <PasswordGate onAuthenticated={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="h-screen w-screen bg-slate-950 text-slate-100 font-sans overflow-hidden relative bg-tactical-grid selection:bg-ops-blue/30 selection:text-ops-blue">
      
      {/* Background Layer */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/50 to-slate-950/90 pointer-events-none" />
      </div>

      {/* Game Manager Dashboard (Admin) */}
      <GameManagerDashboard />

      {/* Main Game UI */}
      {gameStatus === 'playing' && (
        <>
          {viewMode !== 'map' && (
            <GameHeader 
              onHelp={() => setShowHelp(true)} 
              viewMode={viewMode}
              setViewMode={setViewMode}
            />
          )}
          
          <main className="absolute inset-0 top-0 bottom-0 flex overflow-hidden">
            {/* Left Panel: Network/Map */}
            <div className="flex-1 relative">
              {viewMode === 'map' ? (
                <Theater />
              ) : (
                <div className="mt-16 h-full relative">
                  <PopulationNetwork 
                    onNodeSelect={setSelectedNode}
                    selectedNodeId={selectedNode?.id || null}
                  />
                </div>
              )}

              {/* Overlays */}
              <div className="absolute top-4 left-4 z-10 pointer-events-none">
                <MissionScoreboard />
              </div>
              
              <div className="absolute bottom-4 left-4 z-10 pointer-events-none">
                <EventLog />
              </div>

              {/* Network Details Panel (Right side of map view) */}
              <AnimatePresence>
                {viewMode === 'network' && selectedNode && (
                  <motion.div 
                    initial={{ x: 300, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 300, opacity: 0 }}
                    className="absolute top-4 right-4 bottom-4 w-80 z-20 pointer-events-auto"
                  >
                    <NetworkPanel 
                      selectedNode={selectedNode} 
                      onClose={() => setSelectedNode(null)} 
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Right Panel: Actions & Context */}
            <div className="w-96 border-l border-slate-800 bg-slate-900/95 backdrop-blur flex flex-col z-20 shadow-xl">
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {selectedSectorId ? (
                  <ActionPanel />
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-slate-500 p-8 text-center">
                    <Target size={48} className="mb-4 opacity-20" />
                    <p className="text-lg font-medium mb-2">No Sector Selected</p>
                    <p className="text-sm">Select a sector on the map to view available actions and intelligence.</p>
                  </div>
                )}
              </div>
              
              {/* Turn Controls */}
              <div className="p-4 border-t border-slate-800 bg-slate-900">
                <button
                  onClick={endTurn}
                  disabled={isLocked('end_turn')}
                  className={clsx(
                    "w-full py-4 rounded-lg font-bold text-lg tracking-wider transition-all flex items-center justify-center gap-2 shadow-lg",
                    isLocked('end_turn')
                      ? "bg-slate-800 text-slate-500 cursor-not-allowed"
                      : "bg-amber-600 hover:bg-amber-500 text-white hover:shadow-amber-500/20 active:scale-[0.98]"
                  )}
                >
                  <ChevronRight className="animate-pulse" />
                  END TURN
                </button>
              </div>
            </div>
          </main>
        </>
      )}

      {/* Modals & Overlays */}
      <AnimatePresence>
        {gameStatus === 'lobby' && <TeamLobby />}
        {gameStatus === 'briefing' && <WelcomeModal />}
        {gameStatus === 'debrief' && <DebriefModal />}
        {gameStatus === 'adjudication' && (
          <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur flex items-center justify-center">
            {adjudicationPhase === 'resolving' ? (
              <AdjudicationPanel />
            ) : (
              <AdjudicationResults />
            )}
          </div>
        )}
      </AnimatePresence>

      <TurnTransitionOverlay />
      <FeedbackOverlay />
      <ActionBlockedModal />
      <TutorialOverlay />
      
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      
      {/* Multiplayer Chat Overlay - Only show if not in map (CityMap handles it) */}
      {viewMode !== 'map' && (
        <div className="fixed bottom-4 right-4 z-40 pointer-events-none">
          <div className="pointer-events-auto">
            <MultiplayerChat />
          </div>
        </div>
      )}

    </div>
  );
}

export default App;
